import React from 'react';
import PropTypes from 'prop-types';
import { Box, Text } from 'grommet';
import { FormPrevious, FormNext } from 'grommet-icons';
import uniqid from 'uniqid';
import { AppButton, InfoNotice, Spinning } from '@Components/Control';
import NoAuthLayout from '@Components/Layout/NoAuth';
import { useAppTheme } from '@Components/Context';
import {
  upsertOnboardingLead,
  checkEarlyBirdEligibility,
  checkYoutubeCidEligibility,
  downloadOnboardingAgreementPreview,
} from '@Actions/paymentActions';
import { useDispatch } from 'react-redux';
import { fetchReferralInviteByTokenAndEmailApi } from '@Actions/product/creatorReferralActions';
import OnboardingStepper from './OnboardingStepper';
import {
  BasicDetailsStep,
  ServiceSelectionStep,
  CidConfigurationStep,
  SocialConfigurationStep,
  PricingStep,
  AgreementStep,
} from './Steps';
import { StyledRaisedContainer } from './StyledOnboardingComponents';
import { getSocialMonthly, getCidPricing } from './pricingHelpers';

const STEP_KEYS = {
  BASIC: 'basic',
  OFFERINGS: 'offerings',
  CID: 'cid',
  SOCIAL: 'social',
  PRICING: 'pricing',
  AGREEMENT: 'agreement',
};

// Frontend mapping of social platforms to backend enum values
// (backend expects lowercase ids like 'tiktok', 'facebook', 'twitter', etc.)
const SOCIAL_PLATFORM_OPTIONS = [
  { id: 'tiktok', label: 'TikTok' },
  { id: 'instagram', label: 'Instagram' },
  { id: 'facebook', label: 'Facebook' },
  { id: 'dailymotion', label: 'Dailymotion' },
  { id: 'telegram', label: 'Telegram' },
  { id: 'youtube', label: 'YouTube' },
  // Backend expects 'twitter', but we display it as 'X'
  { id: 'twitter', label: 'X' },
  { id: 'pinterest', label: 'Pinterest' },
  { id: 'reddit', label: 'Reddit' },
  { id: 'soundcloud', label: 'SoundCloud' },
  { id: 'linkedin', label: 'LinkedIn' },
  { id: 'blogspot', label: 'Blogspot' },
  { id: 'quora', label: 'Quora' },
  { id: 'tumblr', label: 'Tumblr' },
  { id: 'disqus', label: 'Disqus' },
  { id: 'vk', label: 'VK' },
];

const IDLE_ELIGIBILITY = {
  status: 'idle', data: null, error: null, channelSignature: null,
};

const STEPS = [
  { key: STEP_KEYS.BASIC, label: 'Account Details' },
  { key: STEP_KEYS.OFFERINGS, label: 'Service Selection' },
  { key: STEP_KEYS.CID, label: 'YouTube Content ID' },
  { key: STEP_KEYS.SOCIAL, label: 'Social Platform Monitoring' },
  { key: STEP_KEYS.PRICING, label: 'Plan Summary' },
  { key: STEP_KEYS.AGREEMENT, label: 'Agreement & Signature' },
];

/**
 * Core onboarding wizard shell.
 * Implements:
 *  - Step 1: Basic details
 *  - Step 2: Offerings
 *  - Step 3: YouTube Content ID configuration
 *  - Step 4: Social platform configuration
 *  - Step 5: Pricing summary
 *  - Step 6: Agreement and signature
 */
const OnboardingWizard = ({
  small,
  size,
  loading,
  submitLoading,
  initiateSubmit,
  referralToken = null,
  referralFromUrl = false,
  pricingData = null,
}) => {
  const { theme: { page, button } } = useAppTheme();
  const dispatch = useDispatch();
  const [downloadingAgreement, setDownloadingAgreement] = React.useState(false);
  const [activeStep, setActiveStep] = React.useState(STEP_KEYS.BASIC);
  const [visitedSteps, setVisitedSteps] = React.useState(new Set([STEP_KEYS.BASIC]));

  const navigateTo = (key) => {
    setActiveStep(key);
    setVisitedSteps((prev) => new Set([...prev, key]));
  };
  const [basicDetails, setBasicDetails] = React.useState({
    companyName: '',
    fullName: '',
    email: '',
    phone: '',
    country: '',
  });
  const [offerings, setOfferings] = React.useState({
    useCid: true,
    useSocial: false,
    useWatermarking: false,
  });

  // YouTube Content ID configuration
  const [accountKind, setAccountKind] = React.useState('creator'); // 'creator' | 'agency'
  const [channels, setChannels] = React.useState([]);
  const [csvChannels, setCsvChannels] = React.useState([]);
  const [allowlistChannels, setAllowlistChannels] = React.useState([]);
  const [csvFileName, setCsvFileName] = React.useState('');
  const [uploader, setUploader] = React.useState('redflag'); // 'self' | 'redflag' - default to 'redflag'
  const [cidPlan, setCidPlan] = React.useState('A'); // 'A' | 'B' | null - default to 'A' (Revenue Share)

  // Social scanning configuration
  const [socialPlatforms, setSocialPlatforms] = React.useState([]);
  const [socialMainPlatform, setSocialMainPlatform] = React.useState('');
  const [socialMainLink, setSocialMainLink] = React.useState('');

  // Agreement signature state
  const [signatureBase64, setSignatureBase64] = React.useState('');
  const [signatureSubmitted, setSignatureSubmitted] = React.useState(false);
  const [finalSubmitted, setFinalSubmitted] = React.useState(false);

  // Lead tracking & early bird state
  const [leadId, setLeadId] = React.useState(null);
  const [emailError, setEmailError] = React.useState(null);
  const [inviteToken, setInviteToken] = React.useState(null);
  const [referralExpired, setReferralExpired] = React.useState(false);
  const [leadUpsertPending, setLeadUpsertPending] = React.useState(false);
  const [earlyBirdEligibility, setEarlyBirdEligibility] = React.useState(null);
  const [youtubeEligibility, setYoutubeEligibility] = React.useState(IDLE_ELIGIBILITY);
  const [eligibilityRetryCount, setEligibilityRetryCount] = React.useState(0);
  const earlyBirdCheckedRef = React.useRef(false);
  const youtubeEligibilityRequestRef = React.useRef(0);
  const youtubeEligibilityRef = React.useRef(youtubeEligibility);
  youtubeEligibilityRef.current = youtubeEligibility;
  const normalizedCidChannelsRef = React.useRef(null);
  const emailDebounceRef = React.useRef(null);
  const [nextPending, setNextPending] = React.useState(false);

  const channelLimit = accountKind === 'creator' ? 2 : 100;

  // Combined channels for payload: manual (non-empty) + CSV
  const allChannels = React.useMemo(() => {
    const manual = channels.filter((c) => c && typeof c === 'string' && c.trim() !== '');
    return [...manual, ...csvChannels];
  }, [channels, csvChannels]);
  const normalizedCidChannels = React.useMemo(
    () => allChannels.map((channel) => channel.trim()).filter(Boolean),
    [allChannels],
  );
  normalizedCidChannelsRef.current = normalizedCidChannels;
  const normalizedCidChannelsSignature = React.useMemo(
    () => JSON.stringify(normalizedCidChannels),
    [normalizedCidChannels],
  );

  const visibleSocialPlatforms = offerings.useCid
    ? SOCIAL_PLATFORM_OPTIONS.filter((platform) => platform.id !== 'youtube')
    : SOCIAL_PLATFORM_OPTIONS;

  const socialPlatformLabels = React.useMemo(
    () => SOCIAL_PLATFORM_OPTIONS.reduce((accumulator, platform) => {
      const next = { ...accumulator };
      next[platform.id] = platform.label;
      return next;
    }, {}),
    [],
  );
  const displayPlatforms = socialPlatforms.map((id) => socialPlatformLabels[id] || id);
  const cidPricing = getCidPricing(pricingData, uploader, cidPlan);
  const cidMonthlyFee = cidPricing?.monthlyFeeAmount ?? 0;
  const channelCount = normalizedCidChannels.length;
  const channelMultiplier = offerings.useCid ? channelCount : 1;
  const socialMonthly = getSocialMonthly(
    pricingData, offerings, socialPlatforms, channelMultiplier,
  );
  const estimatedTotal = (offerings.useCid ? cidMonthlyFee * channelCount : 0) + socialMonthly;
  const planDisplayLabel = pricingData?.display?.planLabels?.[cidPlan === 'B' ? 'hybrid' : 'revenueShare'] || '';
  const uploaderDisplayLabel = pricingData?.display?.uploaderLabels?.[uploader] || '';

  // Scroll to top on step change for better UX, especially on mobile
  const scrollContainerRef = React.useRef(null);
  React.useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [activeStep]);

  React.useEffect(() => {
    if (offerings.useCid) {
      setSocialPlatforms((prev) => prev.filter((platformId) => platformId !== 'youtube'));
    }
  }, [offerings.useCid]);

  // Reset channels when account kind changes
  React.useEffect(() => {
    setChannels([]);
    setCsvChannels([]);
    setCsvFileName('');
  }, [accountKind]);

  const isValidEmail = (email) => {
    if (!email.trim()) return false;
    // Simple email pattern; backend will still validate strictly
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  };

  // Clear email error, invite token, and referral expired notice when email changes
  React.useEffect(() => {
    setEmailError(null);
    setInviteToken(null);
    setReferralExpired(false);
  }, [basicDetails.email]);

  // Debounced lead upsert: fires 800ms after a valid email is entered/changed
  React.useEffect(() => {
    if (emailDebounceRef.current) clearTimeout(emailDebounceRef.current);
    if (!isValidEmail(basicDetails.email)) {
      setLeadUpsertPending(false);
      return undefined;
    }
    setLeadUpsertPending(true);
    emailDebounceRef.current = setTimeout(async () => {
      try {
        const result = await upsertOnboardingLead({
          email: basicDetails.email,
          leadId: leadId || undefined,
          contact: basicDetails,
        });
        if (result?.leadId) setLeadId(result.leadId);
      } catch (e) {
        if (e.statusCode === 409) setEmailError(e.message);
        // other errors: silent fail — never block the user
      } finally {
        setLeadUpsertPending(false);
      }

      if (referralToken) {
        try {
          const invite = await fetchReferralInviteByTokenAndEmailApi(
            referralToken, basicDetails.email,
          );
          if (invite) setInviteToken(invite.token || referralToken);
        } catch (e) {
          if (e.statusCode === 404) window.localStorage.removeItem('referralToken');
          if (referralFromUrl) setReferralExpired(true);
        }
      }
    }, 800);
    return () => { if (emailDebounceRef.current) clearTimeout(emailDebounceRef.current); };
  }, [basicDetails.email]);

  // Early bird check: triggered exactly once when the user enters the CID step
  React.useEffect(() => {
    if (activeStep !== STEP_KEYS.CID) return;
    if (earlyBirdCheckedRef.current) return;
    earlyBirdCheckedRef.current = true;

    const run = async () => {
      let currentLeadId = leadId;
      // Fallback: if debounce hasn't fired yet (fast navigation), upsert now
      if (!currentLeadId && isValidEmail(basicDetails.email)) {
        try {
          const upsertResult = await upsertOnboardingLead({
            email: basicDetails.email,
            contact: basicDetails,
          });
          if (upsertResult?.leadId) {
            setLeadId(upsertResult.leadId);
            currentLeadId = upsertResult.leadId;
          }
        } catch (e) { /* silent fail */ }
      }
      if (!currentLeadId) return;
      try {
        const result = await checkEarlyBirdEligibility(currentLeadId);
        setEarlyBirdEligibility(result);
      } catch (e) { /* silent fail */ }
    };
    run();
  }, [activeStep]);

  const retriggerEligibility = React.useCallback(() => {
    setYoutubeEligibility(IDLE_ELIGIBILITY);
    setEligibilityRetryCount((c) => c + 1);
  }, []);

  React.useEffect(() => {
    // Invalidate any in-flight request whenever we take an early-return path,
    // so stale responses from a previous channel set cannot overwrite state.
    const invalidateInflight = () => {
      youtubeEligibilityRequestRef.current += 1;
    };

    if (!offerings.useCid) {
      invalidateInflight();
      setYoutubeEligibility(IDLE_ELIGIBILITY);
      return undefined;
    }

    if (accountKind === 'agency') {
      invalidateInflight();
      setYoutubeEligibility({
        status: 'eligible',
        data: {
          accountType: 'agency',
          checkApplied: false,
          eligible: true,
          minimumSubscribers: 100000,
        },
        error: null,
        channelSignature: normalizedCidChannelsSignature,
      });
      return undefined;
    }

    const currentChannels = normalizedCidChannelsRef.current;
    if (currentChannels.length === 0 || currentChannels.length > 2) {
      invalidateInflight();
      setYoutubeEligibility({
        ...IDLE_ELIGIBILITY, channelSignature: normalizedCidChannelsSignature,
      });
      return undefined;
    }

    // If we already have a result for this exact channel set, keep it
    const { channelSignature: cachedSig, status: cachedStatus } = youtubeEligibilityRef.current;
    if (
      cachedSig === normalizedCidChannelsSignature
      && (cachedStatus === 'eligible' || cachedStatus === 'ineligible')
    ) {
      return undefined;
    }

    const requestId = youtubeEligibilityRequestRef.current + 1;
    youtubeEligibilityRequestRef.current = requestId;

    setYoutubeEligibility({
      status: 'checking',
      data: null,
      error: null,
      channelSignature: normalizedCidChannelsSignature,
    });

    const run = async () => {
      try {
        const result = await checkYoutubeCidEligibility({
          accountType: 'individual',
          channels: currentChannels,
        });
        if (requestId !== youtubeEligibilityRequestRef.current) return;
        setYoutubeEligibility({
          status: result?.eligible ? 'eligible' : 'ineligible',
          data: result,
          error: null,
          channelSignature: normalizedCidChannelsSignature,
        });
      } catch (e) {
        if (requestId !== youtubeEligibilityRequestRef.current) return;
        setYoutubeEligibility({
          status: 'error',
          data: null,
          error: e?.message || 'Could not verify channel eligibility right now. Please retry.',
          channelSignature: normalizedCidChannelsSignature,
        });
      }
    };

    const debounceTimer = setTimeout(run, 600);
    return () => clearTimeout(debounceTimer);
  }, [
    offerings.useCid,
    accountKind,
    normalizedCidChannelsSignature,
    eligibilityRetryCount,
  ]);

  // Hoisted so canGoNext(SOCIAL) can also gate on CID completion when both are selected
  const isCidEligibilitySatisfied = !offerings.useCid
    || accountKind === 'agency'
    || (
      youtubeEligibility.status === 'eligible'
      && youtubeEligibility.channelSignature === normalizedCidChannelsSignature
    );
  const isCidConfigComplete = !offerings.useCid
    || ((channels.filter((c) => c.trim() !== '').length > 0 || csvChannels.length > 0) && cidPlan && isCidEligibilitySatisfied);

  const canGoNext = () => {
    if (activeStep === STEP_KEYS.BASIC) {
      return (
        basicDetails.companyName.trim()
        && basicDetails.fullName.trim()
        && isValidEmail(basicDetails.email)
        && !emailError
        && !leadUpsertPending
      );
    }
    if (activeStep === STEP_KEYS.OFFERINGS) {
      return offerings.useCid || offerings.useSocial || offerings.useWatermarking;
    }
    if (activeStep === STEP_KEYS.CID) {
      if (!offerings.useCid) return true;
      // Must have at least one channel and a plan selected
      const manualChannels = channels.filter((c) => c.trim() !== '');
      const hasChannels = manualChannels.length > 0 || csvChannels.length > 0;
      if (!hasChannels || !cidPlan) {
        return false;
      }

      if (accountKind === 'agency') {
        return true;
      }

      return (
        youtubeEligibility.status === 'eligible'
        && youtubeEligibility.channelSignature === normalizedCidChannelsSignature
      );
    }
    if (activeStep === STEP_KEYS.SOCIAL) {
      if (!offerings.useSocial) return true;
      const hasSocialConfig = socialPlatforms.length > 0 || socialMainLink.trim() !== '';
      // When CID is also selected, require it to be fully configured before leaving Social
      return hasSocialConfig && isCidConfigComplete;
    }
    return true;
  };

  const goNext = async () => {
    if (!canGoNext() || nextPending) return;

    if (activeStep === STEP_KEYS.BASIC) {
      // Await upsert to catch a 409 conflict before navigating
      try {
        setNextPending(true);
        const result = await upsertOnboardingLead({
          email: basicDetails.email,
          leadId: leadId || undefined,
          contact: basicDetails,
        });
        if (result?.leadId) setLeadId(result.leadId);
      } catch (e) {
        if (e.statusCode === 409) {
          setEmailError(e.message);
          return; // Block navigation
        }
        // Other errors: still allow navigation
      } finally {
        setNextPending(false);
      }
      navigateTo(STEP_KEYS.OFFERINGS);
    } else if (activeStep === STEP_KEYS.OFFERINGS) {
      if (offerings.useCid) {
        navigateTo(STEP_KEYS.CID);
      } else if (offerings.useSocial) {
        navigateTo(STEP_KEYS.SOCIAL);
      } else {
        navigateTo(STEP_KEYS.PRICING);
      }
    } else if (activeStep === STEP_KEYS.CID) {
      if (offerings.useSocial) {
        navigateTo(STEP_KEYS.SOCIAL);
      } else {
        navigateTo(STEP_KEYS.PRICING);
      }
    } else if (activeStep === STEP_KEYS.SOCIAL) {
      navigateTo(STEP_KEYS.PRICING);
    } else if (activeStep === STEP_KEYS.PRICING) {
      navigateTo(STEP_KEYS.AGREEMENT);
    }
  };

  const goBack = () => {
    if (activeStep === STEP_KEYS.OFFERINGS) {
      navigateTo(STEP_KEYS.BASIC);
    } else if (activeStep === STEP_KEYS.CID) {
      navigateTo(STEP_KEYS.OFFERINGS);
    } else if (activeStep === STEP_KEYS.SOCIAL) {
      if (offerings.useCid) {
        navigateTo(STEP_KEYS.CID);
      } else {
        navigateTo(STEP_KEYS.OFFERINGS);
      }
    } else if (activeStep === STEP_KEYS.PRICING) {
      if (offerings.useSocial) {
        navigateTo(STEP_KEYS.SOCIAL);
      } else if (offerings.useCid) {
        navigateTo(STEP_KEYS.CID);
      } else {
        navigateTo(STEP_KEYS.OFFERINGS);
      }
    } else if (activeStep === STEP_KEYS.AGREEMENT) {
      navigateTo(STEP_KEYS.PRICING);
    }
  };

  // Final submission handler
  const handleFinalSubmit = () => {
    if (!signatureSubmitted || !signatureBase64) return;
    setFinalSubmitted(true);
    /* eslint-disable camelcase */
    const requestBody = {
      lead_id: leadId || undefined,
      referral_token: inviteToken || undefined,
      contact: {
        company_name: basicDetails.companyName,
        full_name: basicDetails.fullName,
        email: basicDetails.email,
        phone: basicDetails.phone,
        country: basicDetails.country,
      },
      youtube_cid: offerings.useCid
        ? {
          uploader,
          plan: cidPlan === 'B' ? 'hybrid' : 'revenue_share',
          channels: allChannels,
          ...(allowlistChannels.length > 0 ? { allowlist_channels: allowlistChannels } : {}),
        } : undefined,
      account_type: accountKind === 'agency' ? 'agency' : 'individual',
      social_scanning: offerings.useSocial
        ? {
          platforms: socialPlatforms,
          main_platform: socialPlatforms[0] || '',
          main_link: socialMainLink,
        } : undefined,
      watermarking: offerings.useWatermarking,
      agreement: {
        accepted: true,
        signature: signatureBase64,
      },
    };
    /* eslint-enable camelcase */
    initiateSubmit(requestBody);
  };

  // Signature submission handler
  const handleSignatureSubmit = (base64) => {
    setSignatureBase64(base64);
    setSignatureSubmitted(true);
  };

  const handleSignatureReset = () => {
    setSignatureBase64('');
    setSignatureSubmitted(false);
  };

  const handleDownloadAgreement = async () => {
    setDownloadingAgreement(true);
    /* eslint-disable camelcase */
    const payload = {
      contact: {
        company_name: basicDetails.companyName || '',
        full_name: basicDetails.fullName || '',
        email: basicDetails.email || '',
        phone: basicDetails.phone || undefined,
        country: basicDetails.country || undefined,
      },
      account_type: accountKind === 'agency' ? 'agency' : 'individual',
      ...(offerings.useCid ? {
        youtube_cid: {
          uploader,
          plan: cidPlan === 'B' ? 'hybrid' : 'revenue_share',
          channels: allChannels,
          ...(allowlistChannels.length > 0 ? { allowlist_channels: allowlistChannels } : {}),
        },
      } : {}),
      ...(offerings.useSocial ? {
        social_scanning: {
          platforms: socialPlatforms,
          main_platform: socialMainLink ? socialPlatforms[0] || '' : '',
          main_link: socialMainLink || '',
        },
      } : {}),
      watermarking: Boolean(offerings.useWatermarking),
    };
    /* eslint-enable camelcase */
    await dispatch(downloadOnboardingAgreementPreview(payload));
    setDownloadingAgreement(false);
  };

  // --- Stepper access logic ---
  // Step 2: mirrors the Next button condition on Step 1 (bidirectional)
  const isStep1Ready = Boolean(
    basicDetails.companyName.trim()
    && basicDetails.fullName.trim()
    && isValidEmail(basicDetails.email)
    && !emailError,
  );

  // Step 3/4: requires Step 2 to be accessible AND user to have visited it
  const canAccessCid = isStep1Ready && visitedSteps.has(STEP_KEYS.OFFERINGS) && offerings.useCid;
  const canAccessSocial = (
    isStep1Ready && visitedSteps.has(STEP_KEYS.OFFERINGS) && offerings.useSocial
  );

  // Step 5/6: requires visiting all steps in the flow AND config conditions satisfied
  const hasSelectedService = offerings.useCid || offerings.useSocial || offerings.useWatermarking;
  const isSocialConfigComplete = !offerings.useSocial
    || socialPlatforms.length > 0 || socialMainLink.trim() !== '';

  // isCidEligibilitySatisfied / isCidConfigComplete are hoisted above canGoNext()
  // All steps in the user's flow must have been visited before PRICING unlocks
  const hasVisitedFlow = (
    isStep1Ready
    && visitedSteps.has(STEP_KEYS.OFFERINGS)
    && (!offerings.useCid || visitedSteps.has(STEP_KEYS.CID))
    && (!offerings.useSocial || visitedSteps.has(STEP_KEYS.SOCIAL))
  );
  const canAccessPricing = hasVisitedFlow && hasSelectedService
    && isCidConfigComplete && isSocialConfigComplete;

  const disabledSteps = {
    [STEP_KEYS.OFFERINGS]: !isStep1Ready,
    [STEP_KEYS.CID]: !canAccessCid,
    [STEP_KEYS.SOCIAL]: !canAccessSocial,
    [STEP_KEYS.PRICING]: !canAccessPricing,
    [STEP_KEYS.AGREEMENT]: !canAccessPricing,
  };

  // Final submit button is disabled if signature is not submitted or if loading in progress
  const submitDisabled = Boolean(!signatureSubmitted || loading || submitLoading);

  // Helper text explaining why Next is disabled on CID step for creators
  const getNextDisabledReason = () => {
    if (activeStep !== STEP_KEYS.CID || !offerings.useCid || accountKind === 'agency') return null;
    if (youtubeEligibility.status === 'checking') return 'Checking channel eligibility...';
    if (youtubeEligibility.status === 'ineligible') return 'Channel eligibility requirements not met';
    if (youtubeEligibility.status === 'error') return 'Eligibility check failed — please retry';
    if (youtubeEligibility.status === 'idle' && normalizedCidChannels.length > 2) {
      return 'Creators can add up to 2 channels for eligibility verification';
    }
    return null;
  };
  const nextDisabledReason = !canGoNext() ? getNextDisabledReason() : null;

  const renderStepContent = () => {
    if (activeStep === STEP_KEYS.BASIC) {
      return (
        <Box gap="1rem">
          {referralExpired && (
            <InfoNotice
              header="Referral link expired"
              textColor={page.primaryText}
              backgroundColor={page.cardBg}
              borderColor="var(--app-warning)"
              noMargin
              noBottomMargin
            >
              <Text size="0.80rem" color={page.secondaryText} style={{ lineHeight: '1.3' }}>
                This referral link has expired or is no longer valid.
                You&apos;re still welcome to sign up without it.
              </Text>
            </InfoNotice>
          )}
          <BasicDetailsStep
            small={small}
            basicDetails={basicDetails}
            setBasicDetails={setBasicDetails}
            emailError={emailError}
          />
        </Box>
      );
    }

    if (activeStep === STEP_KEYS.OFFERINGS) {
      return (
        <ServiceSelectionStep
          small={small}
          offerings={offerings}
          setOfferings={setOfferings}
          accountKind={accountKind}
          setAccountKind={setAccountKind}
        />
      );
    }

    if (activeStep === STEP_KEYS.CID) {
      return (
        <CidConfigurationStep
          small={small}
          offerings={offerings}
          channels={channels}
          setChannels={setChannels}
          uploader={uploader}
          setUploader={setUploader}
          cidPlan={cidPlan}
          setCidPlan={setCidPlan}
          channelLimit={channelLimit}
          accountKind={accountKind}
          csvChannels={csvChannels}
          setCsvChannels={setCsvChannels}
          csvFileName={csvFileName}
          setCsvFileName={setCsvFileName}
          pricingConfig={pricingData}
          earlyBirdEligibility={earlyBirdEligibility}
          youtubeEligibility={youtubeEligibility}
          onRetryEligibility={retriggerEligibility}
          allowlistChannels={allowlistChannels}
          setAllowlistChannels={setAllowlistChannels}
        />
      );
    }

    if (activeStep === STEP_KEYS.SOCIAL) {
      return (
        <SocialConfigurationStep
          small={small}
          offerings={offerings}
          socialPlatforms={socialPlatforms}
          setSocialPlatforms={setSocialPlatforms}
          socialMainPlatform={socialMainPlatform}
          setSocialMainPlatform={setSocialMainPlatform}
          socialMainLink={socialMainLink}
          setSocialMainLink={setSocialMainLink}
          visibleSocialPlatforms={visibleSocialPlatforms}
          platformLabels={socialPlatformLabels}
        />
      );
    }

    if (activeStep === STEP_KEYS.PRICING) {
      return (
        <PricingStep
          small={small}
          offerings={offerings}
          accountKind={accountKind}
          channels={allChannels}
          uploader={uploader}
          cidPlan={cidPlan}
          socialPlatforms={socialPlatforms}
          socialMainLink={socialMainLink}
          goToStep={(stepKey) => navigateTo(stepKey)}
          setOfferings={setOfferings}
          pricingData={pricingData}
          earlyBirdEligibility={earlyBirdEligibility}
          socialPlatformLabels={socialPlatformLabels}
        />
      );
    }

    if (activeStep === STEP_KEYS.AGREEMENT) {
      return (
        <AgreementStep
          small={small}
          submitLoading={submitLoading}
          offerings={offerings}
          accountKind={accountKind}
          channels={allChannels}
          uploader={uploader}
          cidPlan={cidPlan}
          socialPlatforms={displayPlatforms}
          socialMainLink={socialMainLink}
          pricingData={pricingData}
          basicDetails={basicDetails}
          signaturePreview={signatureBase64}
          onSignatureSubmit={handleSignatureSubmit}
          onSignatureReset={handleSignatureReset}
          signatureSubmitted={signatureSubmitted}
          finalSubmitted={finalSubmitted}
          earlyBirdEligibility={earlyBirdEligibility}
          cidMonthlyFee={cidMonthlyFee}
          socialMonthly={socialMonthly}
          estimatedTotal={estimatedTotal}
          cidPlanDisplayLabel={planDisplayLabel}
          uploaderDisplayLabel={uploaderDisplayLabel}
          onDownloadAgreement={handleDownloadAgreement}
          downloadingAgreement={downloadingAgreement}
        />
      );
    }

    return null;
  };

  const stepperSteps = STEPS.map((s) => {
    const isDisabled = Boolean(disabledSteps[s.key]);
    let tooltip = '';
    if (isDisabled) {
      if (s.key === STEP_KEYS.OFFERINGS) {
        tooltip = 'Please enter your Account Details to continue';
      } else if (s.key === STEP_KEYS.CID) {
        tooltip = 'Enable YouTube Content ID in the Service Selection step to configure this option';
      } else if (s.key === STEP_KEYS.SOCIAL) {
        tooltip = 'Enable Social Scanning in the Service Selection step to configure this option';
      } else if (s.key === STEP_KEYS.PRICING) {
        tooltip = 'Enter your details and select at least one service to view your plan summary';
      } else if (s.key === STEP_KEYS.AGREEMENT) {
        tooltip = 'Complete previous steps to proceed';
      }
    }
    return {
      ...s,
      disabled: isDisabled,
      tooltip,
    };
  });

  return (
    <NoAuthLayout
      removeLowerPad
      altHeaderStyle
      seoConfig={null}
      small={small}
      title="Get started with Redflag"
      subTitle="Follow this short setup to start protecting your content and increasing revenue. Most people finish in a few minutes."
      customWidth={small ? '100%' : '80vw'}
      customGap="1rem"
      justifyContent="start"
      contentTopMargin="5rem"
      bottomPad="2rem"
    >
      <Box
        direction="column"
        gap="1rem"
        width="100%"
        height="100%"
        style={{
          flex: '1 1 0%',
          minHeight: 0,
        }}
      >
        <StyledRaisedContainer
          width="100%"
          bgColor={page.altBg}
          style={{ flexShrink: 0 }}
        >
          <OnboardingStepper
            key={uniqid()}
            small={small}
            size={size}
            steps={stepperSteps}
            activeKey={activeStep}
            onStepClick={(key) => {
              if (!disabledSteps[key]) navigateTo(key);
            }}
          />
        </StyledRaisedContainer>
        <StyledRaisedContainer
          ref={scrollContainerRef}
          bgColor={page.altBg}
          pad="0.5rem"
          gap="1rem"
          width="100%"
          className="onboarding-scroll-container"
          style={{
            maxHeight: small ? '80vh' : '60vh',
            flex: '1 1 auto',
            overflowY: 'scroll',
          }}
        >
          <Box
            style={{
              minHeight: 'fit-content',
              flex: '1 1 auto',
              display: 'flex',
            }}
          >
            {renderStepContent()}
          </Box>
        </StyledRaisedContainer>
        <Box
          direction={small ? 'column' : 'row'}
          justify="between"
          align="center"
          width="100%"
          gap={small ? '1rem' : 0}
          style={{
            flexShrink: 0,
          }}
        >
          <Box
            flex="grow"
            align={small ? 'start' : 'center'}
            justify="start"
            style={{ maxWidth: small ? '100%' : '75%' }}
          >
            <InfoNotice
              header="Need a custom or enterprise plan?"
              textColor={page.primaryText}
              backgroundColor={button.selectBg}
              borderColor={button.primaryBg}
              noMargin
              noBottomMargin
            >
              <Text size="0.80rem" color="var(--secondary-text)" style={{ lineHeight: '1.3' }}>
                These self-serve plans are built for creators, teams, and agencies
                with defined channel and usage needs. For custom onboarding, unlimited
                coverage, higher volumes, or dedicated support, please email&nbsp;
                <a
                  href="mailto:contact@redflagai.co"
                  style={{ color: page.altHighlight, textDecoration: 'underline' }}
                >
                  contact@redflagai.co
                </a>
                &nbsp;or&nbsp;
                <a
                  href="https://go.redflagai.co/discovery-call"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: page.altHighlight, textDecoration: 'underline' }}
                >
                  book a call
                </a>
                &nbsp;to discuss an enterprise plan.
              </Text>
            </InfoNotice>
          </Box>
          {/* Buttons Section */}
          <Box
            direction="column"
            align="end"
            flex={false}
            style={{ flexShrink: 0 }}
            margin={{ top: small ? '1rem' : '0' }}
          >
            <Box
              direction="row"
              gap="1rem"
              align="center"
              justify="end"
            >
              {!(activeStep === STEP_KEYS.BASIC) && (
                <AppButton
                  overrideHover
                  onClick={() => goBack()}
                  disabled={activeStep === STEP_KEYS.BASIC}
                  level="dynamicSmall"
                  bgColor={page.altBg}
                  color={page.primaryText}
                  borderColor={button.border}
                  fontWeight={600}
                  style={{
                    border: `1px solid ${button.border}`,
                  }}
                  label={(
                    <Box direction="row" gap="0.25rem" align="center" pad={{ right: '0.5rem' }}>
                      <Box pad={{ top: '0.15rem' }}>
                        <FormPrevious size="1.25rem" color={page.primaryText} />
                      </Box>
                      <Text color={page.primaryText}>Back</Text>
                    </Box>
                  )}
                />
              )}
              {!(activeStep === STEP_KEYS.AGREEMENT) && (
                <AppButton
                  overrideHover
                  onClick={() => goNext()}
                  disabled={!canGoNext() || nextPending}
                  level="dynamicSmall"
                  style={{ minWidth: 'fit-content' }}
                  color={button.primaryText}
                  bgColor={button.primaryBg}
                  fontWeight={600}
                  label={(
                    <Box direction="row" gap="0.25rem" align="center" pad={{ left: '0.5rem' }}>
                      {activeStep === STEP_KEYS.BASIC && nextPending && (
                        <Box pad={{ top: '0.15rem' }}>
                          <Spinning size="small" color={button.primaryText} />
                        </Box>
                      )}
                      <Text>Next</Text>
                      {!(activeStep === STEP_KEYS.BASIC && nextPending) && (
                        <Box pad={{ top: '0.15rem' }}>
                          <FormNext size="1.25rem" color={button.primaryText} />
                        </Box>
                      )}
                    </Box>
                  )}
                />
              )}
              {activeStep === STEP_KEYS.AGREEMENT && (
                <AppButton
                  overrideHover
                  onClick={handleFinalSubmit}
                  level="dynamicSmall"
                  disabled={submitDisabled}
                  style={{ minWidth: 'fit-content' }}
                  bgColor={button.primaryBg}
                  color={button.primaryText}
                  fontWeight={600}
                  label="Finish"
                />
              )}
            </Box>
            {nextDisabledReason && (
              <Text size="0.75rem" color="var(--app-error)" margin={{ top: '0.25rem' }}>
                {nextDisabledReason}
              </Text>
            )}
          </Box>
        </Box>
      </Box>
    </NoAuthLayout>
  );
};

OnboardingWizard.propTypes = {
  small: PropTypes.bool.isRequired,
  size: PropTypes.string.isRequired,
  initiateSubmit: PropTypes.func,
  referralToken: PropTypes.string,
  referralFromUrl: PropTypes.bool,
  pricingData: PropTypes.object,
  loading: PropTypes.bool.isRequired,
  submitLoading: PropTypes.bool.isRequired,
};

export default OnboardingWizard;
